import React, { useState, useEffect } from 'react';
import { 
  Shield, Users, UserPlus, Edit, Trash2, Save, X, Check, 
  Plus, Search, Lock, Unlock, Crown, Briefcase, Copy,
  ChevronDown, ChevronRight, AlertCircle, Star, TrendingUp,
  Eye, Settings as SettingsIcon
} from 'lucide-react';
import { rolesPermissionsController } from '../../controllers/rolesPermissionsController';
import { authController } from '../../controllers/authController';

const RolesPermissionsPage = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTab, setActiveTab] = useState('roles'); // roles, permissions, statistics
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [newRole, setNewRole] = useState({
    name: '',
    description: '',
    permissions: [],
    color: '#64748b'
  });

  useEffect(() => {
    const user = authController.getCurrentUser();
    setCurrentUser(user);
    loadData();
  }, []);

  const loadData = () => {
    const allRoles = rolesPermissionsController.getAllRoles();
    const allPermissions = rolesPermissionsController.getAllPermissions();
    const stats = rolesPermissionsController.getRoleStatistics();
    
    setRoles(allRoles);
    setPermissions(allPermissions);
    setStatistics(stats);
  };

  const handleCreateRole = () => {
    if (!newRole.name.trim()) {
      alert('Please enter role name');
      return;
    }

    const created = rolesPermissionsController.createRole(newRole);
    
    if (created) {
      loadData();
      setIsCreateModalOpen(false);
      setNewRole({ name: '', description: '', permissions: [], color: '#64748b' });
      alert('Role created successfully!');
    } else {
      alert('Failed to create role');
    }
  };

  const handleUpdateRole = () => {
    if (!selectedRole) return;

    const updated = rolesPermissionsController.updateRole(selectedRole.id, {
      description: selectedRole.description,
      permissions: selectedRole.permissions,
      color: selectedRole.color
    });

    if (updated) {
      loadData();
      setIsEditModalOpen(false);
      setSelectedRole(null);
      alert('Role updated successfully!');
    } else {
      alert('Failed to update role');
    }
  };

  const handleDeleteRole = (roleId) => {
    if (window.confirm('Are you sure you want to delete this role?')) {
      const deleted = rolesPermissionsController.deleteRole(roleId);
      
      if (deleted) {
        loadData();
        alert('Role deleted successfully!');
      } else {
        alert('Failed to delete role. Check if role has users assigned.');
      }
    }
  };

  const handleDuplicateRole = (roleId) => {
    const duplicated = rolesPermissionsController.duplicateRole(roleId);
    
    if (duplicated) {
      loadData();
      alert('Role duplicated successfully!');
    } else {
      alert('Failed to duplicate role');
    }
  };

  const togglePermission = (permissionId, isEditMode = false) => {
    if (isEditMode && selectedRole) {
      const permissions = selectedRole.permissions.includes(permissionId)
        ? selectedRole.permissions.filter(p => p !== permissionId)
        : [...selectedRole.permissions, permissionId];
      
      setSelectedRole({ ...selectedRole, permissions });
    } else {
      const permissions = newRole.permissions.includes(permissionId)
        ? newRole.permissions.filter(p => p !== permissionId)
        : [...newRole.permissions, permissionId];
      
      setNewRole({ ...newRole, permissions });
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const groupedPermissions = permissions.reduce((acc, perm) => {
    if (!acc[perm.category]) {
      acc[perm.category] = [];
    }
    acc[perm.category].push(perm);
    return acc;
  }, {});

  const filteredRoles = roles.filter(role =>
    role.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    role.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getIconForRole = (roleId) => {
    switch (roleId) {
      case 'super_admin': return Crown;
      case 'admin': return Shield;
      case 'manager': return Briefcase;
      default: return Users;
    }
  };

  const styles = {
    container: {
      padding: '24px',
      backgroundColor: '#f8fafc',
      minHeight: '100vh',
    },
    header: {
      marginBottom: '32px',
    },
    title: {
      fontSize: '32px',
      fontWeight: 'bold',
      color: '#1e293b',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    subtitle: {
      fontSize: '16px',
      color: '#64748b',
    },
    tabContainer: {
      display: 'flex',
      gap: '8px',
      marginBottom: '24px',
      borderBottom: '2px solid #e2e8f0',
    },
    tab: {
      padding: '12px 24px',
      backgroundColor: 'transparent',
      border: 'none',
      borderBottom: '3px solid transparent',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: '600',
      color: '#64748b',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    tabActive: {
      color: '#3b82f6',
      borderBottomColor: '#3b82f6',
    },
    actionBar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px',
      gap: '16px',
      flexWrap: 'wrap',
    },
    searchBox: {
      position: 'relative',
      flex: 1,
      minWidth: '300px',
      maxWidth: '500px',
    },
    searchInput: {
      width: '100%',
      padding: '12px 12px 12px 44px',
      backgroundColor: '#ffffff',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      fontSize: '14px',
      outline: 'none',
    },
    searchIcon: {
      position: 'absolute',
      left: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
    },
    createButton: {
      padding: '12px 24px',
      backgroundColor: '#3b82f6',
      color: '#ffffff',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
      gap: '24px',
    },
    roleCard: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.2s',
      position: 'relative',
      border: '2px solid transparent',
    },
    roleCardHover: {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    },
    roleHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      marginBottom: '16px',
    },
    roleIconWrapper: {
      width: '56px',
      height: '56px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    roleInfo: {
      flex: 1,
      marginLeft: '16px',
    },
    roleName: {
      fontSize: '20px',
      fontWeight: 'bold',
      color: '#1e293b',
      marginBottom: '4px',
    },
    roleDescription: {
      fontSize: '14px',
      color: '#64748b',
      marginBottom: '12px',
      lineHeight: '1.5',
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600',
      marginRight: '8px',
    },
    systemBadge: {
      backgroundColor: '#fef3c7',
      color: '#92400e',
    },
    customBadge: {
      backgroundColor: '#dbeafe',
      color: '#1e40af',
    },
    statsRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px',
      padding: '12px',
      backgroundColor: '#f8fafc',
      borderRadius: '8px',
    },
    statLabel: {
      fontSize: '13px',
      color: '#64748b',
      fontWeight: '500',
    },
    statValue: {
      fontSize: '16px',
      fontWeight: 'bold',
      color: '#1e293b',
    },
    permissionsList: {
      marginBottom: '16px',
      maxHeight: '120px',
      overflowY: 'auto',
    },
    permissionTag: {
      display: 'inline-block',
      padding: '4px 10px',
      backgroundColor: '#eff6ff',
      color: '#3b82f6',
      borderRadius: '6px',
      fontSize: '11px',
      fontWeight: '600',
      marginRight: '6px',
      marginBottom: '6px',
    },
    actionButtons: {
      display: 'flex',
      gap: '8px',
      paddingTop: '16px',
      borderTop: '1px solid #e2e8f0',
    },
    iconButton: {
      padding: '8px',
      backgroundColor: 'transparent',
      border: '1px solid #e2e8f0',
      borderRadius: '6px',
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
    },
    modalContent: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      padding: '32px',
      maxWidth: '600px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto',
    },
    modalHeader: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#1e293b',
      marginBottom: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    formGroup: {
      marginBottom: '20px',
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '600',
      color: '#1e293b',
      marginBottom: '8px',
    },
    input: {
      width: '100%',
      padding: '12px',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      fontSize: '14px',
      outline: 'none',
    },
    textarea: {
      width: '100%',
      padding: '12px',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      fontSize: '14px',
      outline: 'none',
      minHeight: '80px',
      resize: 'vertical',
      fontFamily: 'inherit',
    },
    colorPicker: {
      width: '100%',
      height: '48px',
      padding: '4px',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      cursor: 'pointer',
    },
    permissionsGrid: {
      marginTop: '12px',
    },
    categorySection: {
      marginBottom: '16px',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      overflow: 'hidden',
    },
    categoryHeader: {
      padding: '12px 16px',
      backgroundColor: '#f8fafc',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      fontWeight: '600',
      fontSize: '14px',
      color: '#1e293b',
    },
    categoryContent: {
      padding: '12px 16px',
    },
    permissionCheckbox: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '10px',
      marginBottom: '8px',
      borderRadius: '6px',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    checkbox: {
      width: '20px',
      height: '20px',
      cursor: 'pointer',
    },
    permissionInfo: {
      flex: 1,
    },
    permissionName: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#1e293b',
    },
    permissionDesc: {
      fontSize: '12px',
      color: '#64748b',
      marginTop: '2px',
    },
    modalActions: {
      display: 'flex',
      gap: '12px',
      marginTop: '24px',
      paddingTop: '24px',
      borderTop: '2px solid #e2e8f0',
    },
    button: {
      flex: 1,
      padding: '12px 24px',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
    },
    primaryButton: {
      backgroundColor: '#3b82f6',
      color: '#ffffff',
    },
    secondaryButton: {
      backgroundColor: '#e2e8f0',
      color: '#1e293b',
    },
    statsContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '24px',
    },
    statCard: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    },
    statHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '16px',
    },
    statTitle: {
      fontSize: '14px',
      color: '#64748b',
      fontWeight: '500',
    },
    statNumber: {
      fontSize: '32px',
      fontWeight: 'bold',
      color: '#1e293b',
      marginBottom: '8px',
    },
    statDescription: {
      fontSize: '12px',
      color: '#64748b',
    },
    distributionList: {
      marginTop: '16px',
    },
    distributionItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px',
      marginBottom: '8px',
      backgroundColor: '#f8fafc',
      borderRadius: '8px',
    },
    progressBar: {
      width: '100%',
      height: '8px',
      backgroundColor: '#e2e8f0',
      borderRadius: '4px',
      overflow: 'hidden',
      marginTop: '8px',
    },
    progressFill: {
      height: '100%',
      backgroundColor: '#3b82f6',
      transition: 'width 0.3s ease',
    },
  };

  // Check if user has access
  if (!currentUser || (currentUser.role !== 'super_admin' && currentUser.role !== 'admin')) {
    return (
      <div style={styles.container}>
        <div style={{
          backgroundColor: '#fee2e2',
          border: '2px solid #ef4444',
          borderRadius: '12px',
          padding: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px'
        }}>
          <AlertCircle size={32} color="#dc2626" />
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#991b1b' }}>
              Access Denied
            </h3>
            <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#7f1d1d' }}>
              You don't have permission to access Roles & Permissions management.
              Only Super Admins and Admins can manage roles and permissions.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <Shield size={36} color="#3b82f6" />
          Roles & Permissions Management
        </h1>
        <p style={styles.subtitle}>
          Create and manage roles, assign permissions, and control user access
        </p>
      </div>

      {/* Tabs */}
      <div style={styles.tabContainer}>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'roles' && styles.tabActive)
          }}
          onClick={() => setActiveTab('roles')}
        >
          <Shield size={18} />
          Roles
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'permissions' && styles.tabActive)
          }}
          onClick={() => setActiveTab('permissions')}
        >
          <Lock size={18} />
          Permissions
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'statistics' && styles.tabActive)
          }}
          onClick={() => setActiveTab('statistics')}
        >
          <TrendingUp size={18} />
          Statistics
        </button>
      </div>

      {/* Roles Tab */}
      {activeTab === 'roles' && (
        <>
          <div style={styles.actionBar}>
            <div style={styles.searchBox}>
              <Search size={18} color="#94a3b8" style={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search roles..."
                style={styles.searchInput}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button
              style={styles.createButton}
              onClick={() => setIsCreateModalOpen(true)}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
            >
              <Plus size={18} />
              Create New Role
            </button>
          </div>

          <div style={styles.grid}>
            {filteredRoles.map((role) => {
              const RoleIcon = getIconForRole(role.id);
              
              return (
                <div
                  key={role.id}
                  style={styles.roleCard}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                  }}
                >
                  <div style={styles.roleHeader}>
                    <div style={{
                      ...styles.roleIconWrapper,
                      backgroundColor: `${role.color}20`
                    }}>
                      <RoleIcon size={28} color={role.color} />
                    </div>
                  </div>

                  <div style={styles.roleInfo}>
                    <h3 style={styles.roleName}>{role.name}</h3>
                    <div style={{ marginBottom: '12px' }}>
                      <span style={{
                        ...styles.badge,
                        ...(role.isSystem ? styles.systemBadge : styles.customBadge)
                      }}>
                        {role.isSystem ? (
                          <>
                            <Star size={12} />
                            System Role
                          </>
                        ) : (
                          'Custom Role'
                        )}
                      </span>
                    </div>
                    <p style={styles.roleDescription}>{role.description}</p>
                  </div>

                  <div style={styles.statsRow}>
                    <span style={styles.statLabel}>Users</span>
                    <span style={styles.statValue}>
                      <Users size={16} style={{ display: 'inline', marginRight: '6px' }} />
                      {role.userCount}
                    </span>
                  </div>

                  <div style={styles.statsRow}>
                    <span style={styles.statLabel}>Permissions</span>
                    <span style={styles.statValue}>
                      {role.permissions.includes('all') ? (
                        <span style={{ color: '#f59e0b' }}>All Access</span>
                      ) : (
                        <>{role.permissions.length}</>
                      )}
                    </span>
                  </div>

                  {role.permissions.length > 0 && !role.permissions.includes('all') && (
                    <div style={styles.permissionsList}>
                      {role.permissions.slice(0, 5).map(permId => (
                        <span key={permId} style={styles.permissionTag}>
                          {permissions.find(p => p.id === permId)?.name || permId}
                        </span>
                      ))}
                      {role.permissions.length > 5 && (
                        <span style={{ ...styles.permissionTag, backgroundColor: '#f8fafc', color: '#64748b' }}>
                          +{role.permissions.length - 5} more
                        </span>
                      )}
                    </div>
                  )}

                  <div style={styles.actionButtons}>
                    <button
                      style={styles.iconButton}
                      onClick={() => {
                        setSelectedRole(role);
                        setIsEditModalOpen(true);
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#eff6ff'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      title="Edit"
                    >
                      <Edit size={16} color="#3b82f6" />
                    </button>
                    <button
                      style={styles.iconButton}
                      onClick={() => handleDuplicateRole(role.id)}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0fdf4'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      title="Duplicate"
                    >
                      <Copy size={16} color="#10b981" />
                    </button>
                    {!role.isSystem && (
                      <button
                        style={styles.iconButton}
                        onClick={() => handleDeleteRole(role.id)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#fef2f2';
                          e.currentTarget.style.borderColor = '#fecaca';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.borderColor = '#e2e8f0';
                        }}
                        title="Delete"
                      >
                        <Trash2 size={16} color="#ef4444" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Permissions Tab */}
      {activeTab === 'permissions' && (
        <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '24px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '24px' }}>
            All System Permissions
          </h2>
          {Object.entries(groupedPermissions).map(([category, perms]) => (
            <div key={category} style={styles.categorySection}>
              <div
                style={styles.categoryHeader}
                onClick={() => toggleCategory(category)}
              >
                <span>{category} ({perms.length})</span>
                {expandedCategories.includes(category) ? (
                  <ChevronDown size={18} />
                ) : (
                  <ChevronRight size={18} />
                )}
              </div>
              {expandedCategories.includes(category) && (
                <div style={styles.categoryContent}>
                  {perms.map(perm => (
                    <div
                      key={perm.id}
                      style={{
                        ...styles.permissionCheckbox,
                        backgroundColor: '#f8fafc'
                      }}
                    >
                      <Lock size={16} color="#64748b" />
                      <div style={styles.permissionInfo}>
                        <div style={styles.permissionName}>{perm.name}</div>
                        <div style={styles.permissionDesc}>{perm.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Statistics Tab */}
      {activeTab === 'statistics' && statistics && (
        <div>
          <div style={styles.statsContainer}>
            <div style={styles.statCard}>
              <div style={styles.statHeader}>
                <span style={styles.statTitle}>Total Roles</span>
                <Shield size={24} color="#3b82f6" />
              </div>
              <div style={styles.statNumber}>{statistics.totalRoles}</div>
              <div style={styles.statDescription}>
                {statistics.systemRoles} system, {statistics.customRoles} custom
              </div>
            </div>

            <div style={styles.statCard}>
              <div style={styles.statHeader}>
                <span style={styles.statTitle}>Total Users</span>
                <Users size={24} color="#10b981" />
              </div>
              <div style={styles.statNumber}>{statistics.totalUsers}</div>
              <div style={styles.statDescription}>
                Across all roles
              </div>
            </div>

            <div style={styles.statCard}>
              <div style={styles.statHeader}>
                <span style={styles.statTitle}>Total Permissions</span>
                <Lock size={24} color="#8b5cf6" />
              </div>
              <div style={styles.statNumber}>{permissions.length}</div>
              <div style={styles.statDescription}>
                Available system permissions
              </div>
            </div>
          </div>

          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '24px',
            marginTop: '24px'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '20px' }}>
              Role Distribution
            </h3>
            <div style={styles.distributionList}>
              {statistics.roleDistribution.map(item => (
                <div key={item.id} style={styles.distributionItem}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontWeight: '600', fontSize: '14px' }}>{item.name}</span>
                      <span style={{ fontSize: '14px', color: '#64748b' }}>
                        {item.userCount} users ({item.percentage}%)
                      </span>
                    </div>
                    <div style={styles.progressBar}>
                      <div
                        style={{
                          ...styles.progressFill,
                          width: `${item.percentage}%`
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Create Role Modal */}
      {isCreateModalOpen && (
        <div style={styles.modal} onClick={() => setIsCreateModalOpen(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <span>Create New Role</span>
              <X
                size={24}
                style={{ cursor: 'pointer' }}
                onClick={() => setIsCreateModalOpen(false)}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Role Name *</label>
              <input
                type="text"
                style={styles.input}
                placeholder="Enter role name"
                value={newRole.name}
                onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Description</label>
              <textarea
                style={styles.textarea}
                placeholder="Enter role description"
                value={newRole.description}
                onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Color</label>
              <input
                type="color"
                style={styles.colorPicker}
                value={newRole.color}
                onChange={(e) => setNewRole({ ...newRole, color: e.target.value })}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Permissions</label>
              <div style={styles.permissionsGrid}>
                {Object.entries(groupedPermissions).map(([category, perms]) => (
                  <div key={category} style={styles.categorySection}>
                    <div
                      style={styles.categoryHeader}
                      onClick={() => toggleCategory(category)}
                    >
                      <span>{category}</span>
                      {expandedCategories.includes(category) ? (
                        <ChevronDown size={18} />
                      ) : (
                        <ChevronRight size={18} />
                      )}
                    </div>
                    {expandedCategories.includes(category) && (
                      <div style={styles.categoryContent}>
                        {perms.map(perm => (
                          <div
                            key={perm.id}
                            style={{
                              ...styles.permissionCheckbox,
                              backgroundColor: newRole.permissions.includes(perm.id) ? '#eff6ff' : 'transparent'
                            }}
                            onClick={() => togglePermission(perm.id)}
                          >
                            <input
                              type="checkbox"
                              style={styles.checkbox}
                              checked={newRole.permissions.includes(perm.id)}
                              onChange={() => {}}
                            />
                            <div style={styles.permissionInfo}>
                              <div style={styles.permissionName}>{perm.name}</div>
                              <div style={styles.permissionDesc}>{perm.description}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div style={styles.modalActions}>
              <button
                style={{
                  ...styles.button,
                  ...styles.secondaryButton
                }}
                onClick={() => setIsCreateModalOpen(false)}
              >
                <X size={18} />
                Cancel
              </button>
              <button
                style={{
                  ...styles.button,
                  ...styles.primaryButton
                }}
                onClick={handleCreateRole}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
              >
                <Check size={18} />
                Create Role
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Role Modal */}
      {isEditModalOpen && selectedRole && (
        <div style={styles.modal} onClick={() => setIsEditModalOpen(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <span>Edit Role: {selectedRole.name}</span>
              <X
                size={24}
                style={{ cursor: 'pointer' }}
                onClick={() => setIsEditModalOpen(false)}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Role Name</label>
              <input
                type="text"
                style={styles.input}
                value={selectedRole.name}
                disabled={selectedRole.isSystem}
              />
              {selectedRole.isSystem && (
                <p style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                  System role names cannot be changed
                </p>
              )}
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Description</label>
              <textarea
                style={styles.textarea}
                value={selectedRole.description}
                onChange={(e) => setSelectedRole({ ...selectedRole, description: e.target.value })}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Color</label>
              <input
                type="color"
                style={styles.colorPicker}
                value={selectedRole.color}
                onChange={(e) => setSelectedRole({ ...selectedRole, color: e.target.value })}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Permissions</label>
              <div style={styles.permissionsGrid}>
                {Object.entries(groupedPermissions).map(([category, perms]) => (
                  <div key={category} style={styles.categorySection}>
                    <div
                      style={styles.categoryHeader}
                      onClick={() => toggleCategory(category)}
                    >
                      <span>{category}</span>
                      {expandedCategories.includes(category) ? (
                        <ChevronDown size={18} />
                      ) : (
                        <ChevronRight size={18} />
                      )}
                    </div>
                    {expandedCategories.includes(category) && (
                      <div style={styles.categoryContent}>
                        {perms.map(perm => (
                          <div
                            key={perm.id}
                            style={{
                              ...styles.permissionCheckbox,
                              backgroundColor: selectedRole.permissions.includes(perm.id) ? '#eff6ff' : 'transparent'
                            }}
                            onClick={() => togglePermission(perm.id, true)}
                          >
                            <input
                              type="checkbox"
                              style={styles.checkbox}
                              checked={selectedRole.permissions.includes(perm.id)}
                              onChange={() => {}}
                            />
                            <div style={styles.permissionInfo}>
                              <div style={styles.permissionName}>{perm.name}</div>
                              <div style={styles.permissionDesc}>{perm.description}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div style={styles.modalActions}>
              <button
                style={{
                  ...styles.button,
                  ...styles.secondaryButton
                }}
                onClick={() => setIsEditModalOpen(false)}
              >
                <X size={18} />
                Cancel
              </button>
              <button
                style={{
                  ...styles.button,
                  ...styles.primaryButton
                }}
                onClick={handleUpdateRole}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
              >
                <Save size={18} />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesPermissionsPage;